/* eslint-disable quotes */
import React, { useEffect, useMemo, useState } from "react";
import { Autocomplete, CircularProgress, TextField, alpha, useTheme } from "@mui/material";
import { useDebounce } from "../../shared/hooks";
import { useField } from "@unform/core";
import { MunicipiosService } from "../services/api/cadastros/MunicipiosService";
import { styled } from "@mui/material/styles";

type TAutoCompleteOption = {
  idmunicipio: string;
  cidade: string;
  uf: string;
};

interface IAutoCompleteMunicipioProps {
  isExternalLoading?: boolean;
}

// Componente estilizado
const StyledAutocomplete = styled(Autocomplete<TAutoCompleteOption>)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: theme.palette.mode === 'dark' 
      ? alpha(theme.palette.background.default, 0.8)
      : alpha(theme.palette.common.white, 0.8),
    transition: 'all 0.3s ease',
    
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.light, 0.05),
      boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
    },
    
    '&.Mui-focused': {
      backgroundColor: alpha(theme.palette.primary.light, 0.08),
      boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.3)}`,
    },
  },
  
  // Estilo para o popup no modo escuro
  '& .MuiAutocomplete-paper': {
    backgroundColor: theme.palette.mode === 'dark' 
      ? theme.palette.background.paper
      : theme.palette.common.white,
    color: theme.palette.text.primary,
    borderRadius: '12px',
    marginTop: '4px',
    boxShadow: theme.shadows[4],
  },
  
  // Estilo para as opções no modo escuro
  '& .MuiAutocomplete-option': {
    '&:hover': {
      backgroundColor: theme.palette.mode === 'dark' 
        ? alpha(theme.palette.primary.main, 0.2)
        : alpha(theme.palette.primary.main, 0.1),
    },
    '&[aria-selected="true"]': {
      backgroundColor: theme.palette.mode === 'dark' 
        ? alpha(theme.palette.primary.main, 0.3)
        : alpha(theme.palette.primary.main, 0.2),
    },
  },
}));

export const AutoCompleteMunicipio: React.FC<IAutoCompleteMunicipioProps> = ({
  isExternalLoading = false,
}) => {
  const { fieldName, registerField, defaultValue, error, clearError } =
    useField("idmunicipio");
  const theme = useTheme();

  const [selectedId, setSelectedId] = useState<string | undefined>(undefined);

  const [opcoes, setOpcoes] = useState<TAutoCompleteOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [busca, setBusca] = useState("");
  const { debounce } = useDebounce();

  useEffect(() => {
    registerField({
      name: fieldName,
      getValue: () => selectedId,
      setValue: (_, newSelectedId) => setSelectedId(newSelectedId),
    });
  }, [registerField, fieldName, selectedId]);

  useEffect(() => {
    setIsLoading(true);
    debounce(() => {
      MunicipiosService.getAlllist(busca, selectedId).then((result) => {
        setIsLoading(false);
        if (result instanceof Error) {
          alert(result.message);
        } else {
          setOpcoes(result.data.map((municipio) => municipio));
        }
      });
    });
  }, [busca, selectedId, debounce]);

  const autocompleteSelectedOption = useMemo(() => {
    if (!selectedId) return null;

    const selectedOption = opcoes.find(
      (opcao) => opcao.idmunicipio === selectedId,
    );
    if (!selectedOption) return null;

    return selectedOption;
  }, [selectedId, opcoes]);

  return (
    <StyledAutocomplete
      openText="Abrir"
      closeText="Fechar"
      noOptionsText="Sem opções"
      loadingText="Carregando..."
      disablePortal
      value={autocompleteSelectedOption}
      loading={isLoading}
      disabled={isExternalLoading}
      popupIcon={
        isExternalLoading || isLoading ? (
          <CircularProgress size={28} />
        ) : undefined
      }
      onInputChange={(_, newValue) => setBusca(newValue)}
      options={opcoes}
      onChange={(_, newValue) => {
        setSelectedId(newValue?.idmunicipio);
        setBusca("");
        clearError();
      }}
      getOptionLabel={(option) =>
        `${option.idmunicipio} - ${option.cidade} - ${option.uf}`
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label="Município"
          error={!!error}
          size="small"
          helperText={error}
          defaultValue={defaultValue}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '12px',
              backgroundColor: theme.palette.mode === 'dark' 
                ? alpha(theme.palette.background.default, 0.8)
                : alpha(theme.palette.common.white, 0.8),
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.light, 0.05),
              },
              '&.Mui-focused': {
                backgroundColor: alpha(theme.palette.primary.light, 0.08),
              },
            },
          }}
        />
      )}
    />
  );
};